import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TIMELINE } from "../data/story";
import { CHARACTERS } from "./CharacterGallery";
import { ImageSlots } from "./AdminImages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "echo_launch_date", label: "ECHO release date", type: "date", hint: "Drives the ECHO countdown on the book card." },
  { key: "hero_quote", label: "Hero quote", hint: "Default: It's about time." },
  { key: "hero_blurb", label: "Hero paragraph", textarea: true, hint: "Leave empty to keep the current 2046 / 2048 text." },
  { key: "launch_subtitle", label: "Line under \"COGNITO IS HERE\"", hint: "Default: Now reading on Kindle and at Barnes & Noble. It's about time." },
  { key: "social_proof_text", label: "Reader counter sentence", hint: "Text after the live reader number." },
];
const LISTEN_LABELS = { prologue: "Prologue narration", sample: "Chapter Three sample", cognito: "Cognito's voice", dawn: "Dawn's voice", evie: "Evie's voice", adam: "Adam's voice", gage: "Gage's voice", mary: "Mary's voice" };
const BUY_LABELS = { "barnes-noble": "Barnes & Noble", "amazon-kindle": "Amazon Kindle", readerdepot: "ReaderDepot audiobook", "google-books": "Google Books", secondary: "Other stores" };

const EmailResults = ({ emails }) => (
  <div className="glass rounded-3xl border border-white/10 p-6" data-testid="admin-email-results">
    <h3 className="font-orbitron text-xs uppercase tracking-[0.25em] text-cyan-300 mb-4">Email results</h3>
    <div className="grid md:grid-cols-2 gap-3">
      {[["ch4", "Chapter Four email", [["site", "Clicked “Keep reading”"]]], ["ch5", "Chapter Five email", [["barnes-noble", "Clicked Barnes & Noble"], ["amazon-kindle", "Clicked Amazon Kindle"]]]].map(([key, title, rows]) => (
        <div key={key} className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid={`admin-email-${key}`}>
          <p className="text-[11px] uppercase tracking-widest text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-white mt-1 tabular-nums" data-testid={`admin-email-${key}-sent`}>{emails[key].sent} <span className="text-xs text-gray-400 font-sans">sent</span></p>
          {rows.map(([t, label]) => (
            <p key={t} className="text-sm text-gray-300 mt-1" data-testid={`admin-email-${key}-${t}`}>{label}: <span className="text-cyan-300 font-semibold">{emails[key].clicks[t]}</span></p>
          ))}
        </div>
      ))}
    </div>
    <p className="text-xs text-gray-500 mt-3">Replies to these emails go straight to your Gmail inbox.</p>
  </div>
);

const toDateInput = (v) => (v ? String(v).slice(0, 10) : "");
const INPUT = "w-full px-4 py-3 rounded-2xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-cyan-400/60";
const mergeTimeline = (saved) => TIMELINE.map((t, i) => ({ year: t.year, title: t.title, text: t.text, ...((saved || [])[i] || {}) }));
const mergeCharacters = (saved) => CHARACTERS.map((c) => ({ id: c.id, name: c.name, role: c.role, bio: c.bio, ...((saved || []).find((o) => o.id === c.id) || {}) }));

const ListEditor = ({ title, items, fields, onChange, onReset, testId }) => (
  <div className="glass rounded-3xl border border-white/10 p-6 space-y-4" data-testid={testId}>
    <div className="flex items-center justify-between gap-3">
      <h3 className="font-orbitron text-xs uppercase tracking-[0.25em] text-cyan-300">{title}</h3>
      <button type="button" onClick={onReset} className="text-xs text-gray-400 hover:text-white underline" data-testid={`${testId}-reset`}>Restore original text</button>
    </div>
    {items.map((item, i) => (
      <div key={i} className="rounded-2xl bg-white/5 border border-white/10 p-4 space-y-2" data-testid={`${testId}-item-${i}`}>
        {fields.map((f) => (
          <label key={f.key} className="block">
            <span className="block text-xs text-gray-400 mb-1">{f.label}</span>
            {f.textarea
              ? <textarea rows={3} value={item[f.key] || ""} onChange={(e) => onChange(i, f.key, e.target.value)} className={INPUT} data-testid={`${testId}-${i}-${f.key}`} />
              : <input value={item[f.key] || ""} onChange={(e) => onChange(i, f.key, e.target.value)} className={INPUT} data-testid={`${testId}-${i}-${f.key}`} />}
          </label>
        ))}
      </div>
    ))}
  </div>
);

const StatGrid = ({ title, data, labels, testId }) => (
  <div className="glass rounded-3xl border border-white/10 p-6" data-testid={testId}>
    <h3 className="font-orbitron text-xs uppercase tracking-[0.25em] text-cyan-300 mb-4">{title}</h3>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {Object.entries(labels).filter(([k]) => k in (data || {})).map(([k, label]) => (
        <div key={k} className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid={`${testId}-${k}`}>
          <p className="text-[11px] uppercase tracking-widest text-gray-500">{label}</p>
          <p className="text-3xl font-bold text-white mt-1 tabular-nums">{data[k]}</p>
        </div>
      ))}
    </div>
  </div>
);

export const SiteAdmin = ({ authHeaders, onOpenSubs }) => {
  const [form, setForm] = useState({});
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => {
      const d = r.data || {};
      setForm({ ...d, timeline: mergeTimeline(d.timeline), characters: mergeCharacters(d.characters) });
    }).catch(() => {});
    axios.get(`${API}/admin/stats`, authHeaders).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/admin/subscribers`, authHeaders).then((r) => setSubs(r.data)).catch(() => {});
  }, [authHeaders]);

  const editList = (key) => (i, field, value) =>
    setForm((s) => ({ ...s, [key]: s[key].map((it, j) => (j === i ? { ...it, [field]: value } : it)) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
      const payload = {
        ...form,
        timeline: same(form.timeline, mergeTimeline(null)) ? null : form.timeline,
        characters: same(form.characters, mergeCharacters(null)) ? null : form.characters,
        echo_launch_date: form.echo_launch_date ? `${toDateInput(form.echo_launch_date)}T12:00:00-04:00` : null };
      const r = await axios.put(`${API}/admin/settings`, payload, authHeaders);
      setForm({ ...r.data, timeline: mergeTimeline(r.data.timeline), characters: mergeCharacters(r.data.characters) });
      toast.success("Site updated — changes are live.");
    } catch {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-site">
      {stats && <StatGrid title="Listens" data={stats.listen} labels={LISTEN_LABELS} testId="admin-listens" />}
      {stats && (
        <div className="space-y-2">
          <StatGrid title="Buy-button clicks" data={stats.buy} labels={BUY_LABELS} testId="admin-buys" />
          <p className="text-xs text-gray-500 px-2">Purchases happen on each retailer's own site, so buyer names aren't shared with us. These are clicks to each store. For actual sales, check your Amazon KDP and Barnes &amp; Noble Press dashboards.</p>
        </div>
      )}
      {stats?.emails && <EmailResults emails={stats.emails} />}
      {subs && (
        <div className="glass rounded-3xl border border-white/10 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="admin-site-emails">
          <div>
            <h3 className="font-orbitron text-xs uppercase tracking-[0.25em] text-cyan-300 mb-1">Emails shared</h3>
            <p className="text-white text-2xl font-orbitron">{subs.total}</p>
            <p className="text-gray-500 text-xs mt-1">Latest: {(subs.subscribers || []).slice(0, 3).map((s) => s.email).join(", ") || "—"}</p>
          </div>
          <button type="button" onClick={onOpenSubs} className="px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-sm text-white" data-testid="admin-site-view-emails">View all emails</button>
        </div>
      )}
      <ImageSlots authHeaders={authHeaders} />
      <form onSubmit={save} className="glass rounded-3xl border border-cyan-400/20 p-6 space-y-5" data-testid="admin-settings-form">
        <h3 className="font-orbitron text-xs uppercase tracking-[0.25em] text-cyan-300">Edit the page</h3>
        {FIELDS.map((f) => {
          const common = {
            value: f.type === "date" ? toDateInput(form[f.key]) : form[f.key] || "",
            onChange: (e) => setForm((s) => ({ ...s, [f.key]: e.target.value })),
            className: INPUT,
            "data-testid": `admin-setting-${f.key}`,
          };
          return (
            <label key={f.key} className="block">
              <span className="block text-sm text-gray-300 mb-1.5">{f.label}</span>
              {f.textarea ? <textarea rows={3} {...common} /> : <input type={f.type || "text"} {...common} />}
              <span className="block text-xs text-gray-500 mt-1">{f.hint}</span>
            </label>
          );
        })}
        {form.timeline && (
          <ListEditor
            title="Story timeline"
            testId="admin-timeline-editor"
            items={form.timeline}
            fields={[{ key: "year", label: "Year" }, { key: "title", label: "Title" }, { key: "text", label: "Text", textarea: true }]}
            onChange={editList("timeline")}
            onReset={() => setForm((s) => ({ ...s, timeline: mergeTimeline(null) }))}
          />
        )}
        {form.characters && (
          <ListEditor
            title="Character bios"
            testId="admin-characters-editor"
            items={form.characters}
            fields={[{ key: "name", label: "Name" }, { key: "role", label: "Role" }, { key: "bio", label: "Bio", textarea: true }]}
            onChange={editList("characters")}
            onReset={() => setForm((s) => ({ ...s, characters: mergeCharacters(null) }))}
          />
        )}
        <button type="submit" disabled={saving} className="btn-gradient text-white px-8 py-3 rounded-full font-semibold disabled:opacity-60" data-testid="admin-settings-save">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
};
